/**
 * navGroups — grouped Control-Room navigation model for the admin home.
 *
 * Pure config. Every `target` is an EXISTING admin tab id (see tabsConfig.js /
 * Admin.jsx switch) so all routes, panels, permissions and badges are
 * preserved — this is a presentation-only regrouping of the previously flat
 * tab bar into a card-based, mobile-first dashboard.
 *
 * `commsTab` (optional) deep-links into a sub-tab of the Communications panel.
 * `badge` (optional) names which live counter the tile should surface.
 */
import {
  Users, Activity, Map as MapIcon, List, Cog, HeartPulse, UserCheck,
  Sparkles, Upload, Rocket, Contact, LayoutDashboard, Gauge, ShieldCheck,
  Building2, MessageSquare, CreditCard, Megaphone, UserPlus, Server, Bot,
  Presentation, ShieldAlert, Wand2, PhoneCall, PhoneMissed, MailCheck,
} from "lucide-react";
import { SHOW_LAUNCH_TOOLS, SHOW_DEMO_TOOLS } from "./tabsConfig";

// Design-agent accent palette (on-brand: black/white + green, with vivid
// group accents drawn from the mockup).
export const ACCENTS = {
  blue: "#0062FF",
  green: "#00C853",
  orange: "#FF6B00",
  purple: "#6D28D9",
  teal: "#0F766E",
};

// Quick Access — the five highest-frequency operational destinations.
export const QUICK_ACCESS = [
  { target: "dashboard", label: "Dashboard", icon: LayoutDashboard, accent: ACCENTS.blue },
  { target: "copilot",   label: "AI Copilot", icon: Bot,            accent: ACCENTS.purple },
  { target: "ai-booking", label: "AI Booking", icon: Wand2,         accent: ACCENTS.blue },
  { target: "voice-tuning", label: "Voice AI",  icon: PhoneCall,     accent: ACCENTS.teal },
  { target: "map",       label: "Map",       icon: MapIcon,         accent: ACCENTS.green },
  { target: "rides",     label: "Rides",     icon: List,            accent: ACCENTS.orange, badge: "rides" },
  { target: "fleet",     label: "Fleet",     icon: Users,           accent: ACCENTS.teal },
  { target: "readiness", label: "Launch",    icon: Rocket,          accent: ACCENTS.purple },
];

// Grouped sections beneath Quick Access.
export const NAV_GROUPS = [
  {
    key: "customer", title: "Customer & Dispatch", accent: ACCENTS.blue,
    items: [
      { target: "customers", label: "Customers",   icon: Contact },
      { target: "approvals", label: "Approvals",   icon: UserCheck },
      { target: "inbox",     label: "Inbox",       icon: MessageSquare, badge: "inbox" },
      { target: "missed-calls", label: "Missed Calls", icon: PhoneMissed },
      { target: "health",    label: "Ride Health", icon: HeartPulse,    badge: "health" },
      { target: "fatigue",   label: "Fatigue",     icon: Gauge,         badge: "fatigue" },
      { target: "safety",    label: "Safety / SOS", icon: ShieldAlert,  badge: "safety" },
    ],
  },
  {
    key: "business", title: "Business", accent: ACCENTS.green,
    items: [
      { target: "corporate",     label: "Corporate",     icon: Building2 },
      { target: "corp-apps",     label: "Applications",  icon: UserPlus, badge: "corpApps" },
      { target: "subscriptions", label: "Subscriptions", icon: CreditCard },
      { target: "imports",       label: "Imports",       icon: Upload },
    ],
  },
  {
    key: "compliance", title: "Compliance & Documents", accent: ACCENTS.orange,
    items: [
      { target: "compliance", label: "Compliance", icon: ShieldCheck, badge: "compliance" },
    ],
  },
  {
    key: "comms", title: "Communications", accent: ACCENTS.purple,
    items: [
      { target: "comms", label: "Communications", icon: Megaphone, commsTab: "campaigns" },
      { target: "comms", label: "Recruitment",    icon: UserPlus,  commsTab: "recruitment" },
      { target: "confirmations", label: "Confirmations", icon: MailCheck },
    ],
  },
  {
    key: "system", title: "System", accent: ACCENTS.teal,
    items: [
      { target: "onboarding", label: "Setup",    icon: Sparkles },
      { target: "system",     label: "System",   icon: Activity },
      { target: "settings",   label: "Settings", icon: Cog },
      // Internal operator-provisioning generator — only when launch tools are on.
      ...(SHOW_LAUNCH_TOOLS ? [{ target: "provisioning", label: "Provisioning", icon: Server }] : []),
      // Sales Demonstration Environment — only in demo/preview builds.
      ...(SHOW_DEMO_TOOLS ? [{ target: "demo", label: "Demo Env", icon: Presentation }] : []),
    ],
  },
];
